import React from 'react';
import MainLayout from '@/components/layout/MainLayout';
import DrawingCanvas from '@/components/canvas/DrawingCanvas';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { v4 as uuidv4 } from 'uuid';
import { useAuth } from '@/contexts/AuthContext';

const Canvas = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { addChat, addMessage, models } = useSupabaseStore();
  const { user } = useAuth();

  const handleSaveCanvas = async (imageData: string) => {
    // Use Supabase store to create chat and add message
    const defaultModelId = models[0]?.id || '';
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to create a chat.",
        variant: "destructive"
      });
      return;
    }

    // Create a new chat in Supabase
    const chatId = await addChat(user.id, 'Canvas Drawing', defaultModelId);
    if (!chatId) {
      toast({
        title: "Error",
        description: "Failed to create chat for canvas image.",
        variant: "destructive"
      });
      return;
    }

    // Save the canvas drawing as a type: 'image' attachment (not 'canvas')
    const attachment = {
      id: uuidv4(),
      type: 'image' as const,
      content: imageData,
      name: 'Canvas drawing'
    };

    // Add a user message with the attachment to the new chat in Supabase
    await addMessage(chatId.id, {
      content: "I've shared a drawing:",
      role: 'user',
      sender: user.id,
      attachments: [attachment],
    });

    // Navigate to the new chat (Supabase chat page will now see the message)
    navigate(`/chat/${chatId.id}`);

    toast({
      title: "Canvas saved",
      description: "Your drawing has been sent to chat",
    });
  };

  return (
    <MainLayout>
      <div className="h-full flex flex-col">
        <div className="p-4 border-b">
          <h1 className="text-2xl font-bold">Canvas</h1>
          <p className="text-muted-foreground">
            Draw, sketch, and create visual content with AI-assisted backgrounds from DALL-E
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Note: You'll need your own OpenAI API key to generate backgrounds
          </p>
        </div>
        
        <div className="flex-1 overflow-hidden">
          <DrawingCanvas onSave={handleSaveCanvas} />
        </div>
      </div>
    </MainLayout>
  );
};

export default Canvas;